import { memo, type HTMLAttributes } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { placed, placedVariants } from '@/lib/style-guard'
import { contentClasses, type ContentProps } from '@/lib/content-props'

const badgeLook = cva(
  [
    'label-fit inline-flex items-center gap-1 font-medium rounded-[var(--badge-radius)]',
    '[&_svg]:shrink-0',
  ].join(' '),
  {
    variants: {
      variant: {
        success: 'bg-[var(--status-success-bg)] text-[var(--status-success-text)]',
        warning: 'bg-[var(--status-warning-bg)] text-[var(--status-warning-text)]',
        error: 'bg-[var(--status-error-bg)] text-[var(--status-error-text)]',
        info: 'bg-[var(--badge-info-bg)] text-[var(--status-info-text)]',
        neutral: 'bg-[var(--badge-neutral-bg)] text-[var(--text-secondary)]',
        /** The action role: something new for you (an unread count). Follows a tenant's brand. */
        action: 'bg-[var(--surface-brand-subtle)] text-[var(--text-on-brand-subtle)]',
        /* D-12 · `premium` is gone. It was the app's only gradient chip, and it
           wore --color-primary-600 → --color-accent-500, i.e. BOTH accents at
           once, on a word («Pro», «Tilbudsmodus») that names a capacity. Two
           rules said no: the directive's NEVER-list («gradients as decoration»,
           «more than one accent»), and husregel 8 — a severity or an accent
           never stands on a category, a role or a capacity; if such a label
           needs distinction it gets an ICON, not a colour. All five call sites
           now pass `neutral` and carry a Crown/Sparkles glyph instead. */
        /**
         * For badges overlaid on photos (event cover-image, etc.). Always
         * readable regardless of background: dark semi-transparent fill +
         * blur + white text + soft white ring. Instagram/WhatsApp pattern.
         */
        overlay:
          'bg-[var(--scrim-medium)] text-[var(--text-on-media)] backdrop-blur-md ring-1 ring-[var(--border-on-media-subtle)]',
      },
      size: {
        /**
         * `sm` is a TIGHTER badge, not a smaller-than-legible one. It was
         * `text-2xs` — 11px, under the 12.5px the mobile law fixes as the
         * floor — which put every «Publisert» / «Utkast» chip in the app below
         * it. The size variants now vary density (padding) and keep the text
         * readable; layout-gate found this on /events and /dashboard.
         */
        sm: 'px-2 py-px text-small [&_svg]:size-3',
        default: 'px-3 py-0.5 text-small [&_svg]:size-3.5',
        lg: 'px-4 py-1 text-label [&_svg]:size-4',
      },
    },
    defaultVariants: {
      variant: 'neutral',
      size: 'default',
    },
  }
)

interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeLook> {
  onRemove?: () => void
}

const Badge = memo(function Badge({ className, align, clamp, wrap, numeric, focus, variant, size, children, onRemove, ...props }: (BadgeProps) & ContentProps) {
  const { t } = useTranslation()
  return (
    <span className={cn(badgeLook({ variant, size }), contentClasses({ align, clamp, wrap, numeric, focus }), placed(className, 'Badge', props))} {...props}>
      {children}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="ml-0.5 rounded-full p-0.5 hover:bg-current/10 transition-colors cursor-pointer"
          aria-label={t('common.remove')}
        >
          <X className="size-3" />
        </button>
      )}
    </span>
  )
})

const BadgeDot = memo(function BadgeDot({
  className,
  animated = false,
  color = 'var(--color-success-500)',
}: {
  className?: string
  animated?: boolean
  color?: string
}) {
  return (
    <span
      className={cn(
        'inline-block size-2 rounded-full',
        animated && 'skeleton-blink',
        placed(className, 'BadgeDot')
      )}
      style={{ backgroundColor: color }}
    />
  )
})

/** The look, for an element that is not a Badge. Its `className` option is a caller's, checked like the prop. */
const badgeVariants = placedVariants(badgeLook, 'badgeVariants')

export { Badge, BadgeDot, badgeVariants }
export type { BadgeProps }
